/****************************************************
 * Module Name:  ProcessMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";
import Constants from "../../utility/constants";
import { Box, Tooltip, Button, CircularProgress, Chip, Avatar } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import renderCellExpand from "../common/dataGridCellExpand";
import CommonSnackbar from "../common/commonSnackBar";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import AddMfgProcessModal from "./addMfgProcessModal";
import ProcessMasterProxy from "../../utility/proxy/manager/manufacturingProxy/processMasterProxy";
import Utility from "../../utility/utility";

const ProcessMaster = () => {
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [processList, setProcessList] = useState([]);
    const [access, setAccess] = useState(false);
    const [processListLoader, setProcessListLoader] = useState(false);
    const [sourceData, setSourceData] = useState([]);
    const [editOperation, setEditOperation] = useState(false);
    const [processSuccessSnackbar, setProcessSuccessSnackbar] = useState(false);
    const [showAddProcessModal, setShowAddProcessModal] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [processOperation, setProcessOperation] = useState('create');

    useEffect(() => {
        PageTitle("MANUFACTURING_MASTER_PROCESS");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_MASTER_PROCESS
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            handleSearch();
        }
    }

    const handleSearch = () => {
        setProcessListLoader(true);
        ProcessMasterProxy.GetAllProcessDetails((responseData) => {
            if (responseData) {
                setProcessList(responseData);
                setProcessListLoader(false);
            } else {
                setProcessListLoader(false);
                setErrorSnackbar(true);
            }
        });
    }

    const addOrUpdateProcess = (processData) => {
        setLoadingButton(true);
        if (editOperation) {
            ProcessMasterProxy.UpdateProcess(processData, (response) => {
                if (response) {
                    setLoadingButton(false);
                    handleSearch();
                    setProcessSuccessSnackbar(true);
                    setProcessOperation('update');
                    setShowAddProcessModal(false);
                } else {
                    setLoadingButton(false);
                    setErrorSnackbar(true);
                }
            });
        } else {
            ProcessMasterProxy.AddProcess(processData, (response) => {
                if (response) {
                    setLoadingButton(false);
                    handleSearch();
                    setProcessSuccessSnackbar(true);
                    setProcessOperation('create');
                    setShowAddProcessModal(false);
                } else {
                    setLoadingButton(false);
                    setErrorSnackbar(true);
                }
            });
        }
    }

    const handleClose = () => {
        setProcessSuccessSnackbar(false);
    };

    const initialColumns = () => [
        {
            field: "processid",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "processname",
            headerName: "NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "description",
            headerName: "DESCRIPTION",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalDate(params.value),
        },
        {
            field: "nextprocess",
            headerName: "NEXT PROCESS",
            headerAlign: "center",
            align: "center",
            flex: 3,
            renderCell: (params) => (
                <Box sx={{ minHeight: "auto", mt: 1 }}>
                    {params.value.map((process, index) => (
                        <Chip
                            key={index}
                            avatar={<Avatar>{process.id}</Avatar>}
                            label={process.name}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    ))}
                </Box>
            ),
            sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "name")
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const rowData = { ...params.row };
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddProcessModal(true)
                            setSourceData(rowData)
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    const columns = initialColumns();

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            {processSuccessSnackbar ? (
                <CommonSnackbar
                    show={processSuccessSnackbar}
                    message={`Process ${processOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}

            {showAddProcessModal ? (
                <AddMfgProcessModal
                    Show={true}
                    handleClose={() => setShowAddProcessModal(false)}
                    handleSave={(data) => addOrUpdateProcess(data)}
                    editOperation={editOperation}
                    sourceData={sourceData}
                    loading={loadingButton}
                />
            ) : null}

            {
                access ? <NoPermission /> : (
                    <Box px={1} mt={4}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "85vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                density="compact"
                                loading={processListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: GridToolbar }}
                                rows={processList}
                                columns={columns}
                                getRowId={(row) => row.processid}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            processid: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: "270px",
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    alignItems: "center"
                                }}>
                                {processListLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            color="primary"
                                            fontSize="medium"
                                            onClick={() => handleSearch()}
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip title="Add Process">
                                    <Button
                                        color="info"
                                        variant="outlined"
                                        size="small"
                                        style={{ marginRight: "10px", marginLeft: "10px" }}
                                        onClick={() => {
                                            setEditOperation(false)
                                            setShowAddProcessModal(true)
                                            setSourceData([])
                                        }
                                        }
                                        startIcon={<AddBoxOutlinedIcon size="large" />}
                                    >
                                        Process
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                )
            }
        </Box >
    )
}
export default ProcessMaster;